import { css, cx } from "@emotion/css";
import { IconButton } from "@octopusdeploy/design-system-components";
import { text, themeTokens, space } from "@octopusdeploy/design-system-tokens";
import type { NamedResource, KubernetesLiveStatusResource, KubernetesMachineLiveStatusResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { keyBy } from "lodash";
import * as React from "react";
import { K8sResourceIcon } from "~/areas/projects/components/Observability/KubernetesResources";
import { LiveStatusResourceIcon, MachineStatusIcon } from "~/areas/projects/components/Observability/LiveStatusIcons";
import InternalLink from "~/components/Navigation/InternalLink";
import { DataTable, DataTableBody, DataTableHeader, DataTableRow, DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable/index";
type OnResourceSelected = (resource: KubernetesLiveStatusResource) => void;
type LiveStatusTableProps = {
    spaceId: string;
    deploymentTargets: NamedResource[];
    machineLiveStatusResources: KubernetesMachineLiveStatusResource[];
    resourceSelected: OnResourceSelected;
};
const emptyNamespaceDisplay = "-";
// Helper functions for auto-expansion logic
const FAILING_HEALTH_STATUSES = ["Degraded", "Missing", "Unknown"] as const;
const isFailingHealthStatus = (healthStatus: string): boolean => {
    return FAILING_HEALTH_STATUSES.some((status) => status === healthStatus);
};
const hasFailingResourcesRecursively = (resource: KubernetesLiveStatusResource): boolean => {
    if (!resource)
        return false;
    const isFailing = isFailingHealthStatus(resource.HealthStatus);
    const hasFailingChildren = resource.Children?.some((child) => hasFailingResourcesRecursively(child)) ?? false;
    return isFailing || hasFailingChildren;
};
const useAutoExpansion = (shouldExpand: boolean) => {
    const [open, setOpen] = React.useState(shouldExpand);
    React.useEffect(() => {
        if (shouldExpand && !open) {
            setOpen(true);
        }
    }, [shouldExpand, open]);
    return [open, setOpen] as const;
};
// Note: Added these for future dynamic sorting capabilities
type ResourceSortFunction = (a: KubernetesLiveStatusResource, b: KubernetesLiveStatusResource) => number;
const resourceSortFunctions: {
    [key: string]: ResourceSortFunction;
} = {
    byName: (a, b) => a.Name.localeCompare(b.Name),
    byHealthStatus: (a, b) => a.HealthStatus.localeCompare(b.HealthStatus),
    // null values go at the end of the alphabet
    bySyncStatus: (a, b) => (a.SyncStatus ?? "z").localeCompare(b.SyncStatus ?? "z"),
    byKind: (a, b) => a.Kind.localeCompare(b.Kind),
    byNameSpace: (a, b) => (a.Namespace ?? emptyNamespaceDisplay).localeCompare(b.Namespace ?? emptyNamespaceDisplay),
};
export function LiveStatusTable({ spaceId, deploymentTargets, machineLiveStatusResources, resourceSelected }: LiveStatusTableProps) {
    const machineIndexedKubernetesObjects = keyBy(machineLiveStatusResources, (r) => r.MachineId);
    const machineIndexedDeploymentTargets = keyBy(deploymentTargets, (r) => r.Id);
    return (<DataTable title="Live Objects">
            <DataTableHeader>
                <DataTableRow>
                    <HeaderCell text="name"/>
                    <HeaderCell text="kind"/>
                    <HeaderCell text="ready"/>
                    <HeaderCell text="namespace"/>
                    <HeaderCell text="sync status"/>
                    <HeaderCell text="health status"/>
                </DataTableRow>
            </DataTableHeader>
            <DataTableBody>
                {Object.keys(machineIndexedKubernetesObjects).map((machineId) => (<MachineAndChildren key={machineId} spaceId={spaceId} target={machineIndexedDeploymentTargets[machineId] === undefined ? { Name: machineId, Id: machineId } : machineIndexedDeploymentTargets[machineId]} machineResources={machineIndexedKubernetesObjects[machineId]} startExpanded={Object.keys(machineIndexedKubernetesObjects).length === 1} resourceSelected={resourceSelected}/>))}
            </DataTableBody>
        </DataTable>);
}
function HeaderCell(props: {
    text: string;
}) {
    return (<DataTableRowColumn>
            <h2 className={styles.headerRow}>{props.text}</h2>
        </DataTableRowColumn>);
}
type MachineAndChildrenProps = {
    spaceId: string;
    target: {
        Name: string;
        Id: string;
    };
    machineResources: KubernetesMachineLiveStatusResource;
    startExpanded: boolean;
    resourceSelected: OnResourceSelected;
};
function MachineAndChildren({ spaceId, target, machineResources, startExpanded, resourceSelected }: MachineAndChildrenProps) {
    // Check if any resources in this machine have failing health status
    const hasFailingResources = React.useMemo(() => {
        return machineResources.Resources.some((resource) => hasFailingResourcesRecursively(resource));
    }, [machineResources.Resources]);
    const shouldStartExpanded = startExpanded || hasFailingResources;
    const [open, setOpen] = useAutoExpansion(shouldStartExpanded);
    const linkToMachine = links.deploymentTargetSettingsPage.generateUrl({ spaceId: spaceId, machineId: target.Id });
    // Hide empty targets
    if (machineResources.Resources.length === 0) {
        return <></>;
    }
    return (<>
            <DataTableRow className={styles.machineRow}>
                <DataTableRowColumn colSpan={4} fullWidth={true} fullHeight={false} key={target.Id}>
                    <div className={styles.multiItemCellFlexContainer}>
                        <CollapseIconOrSpacer name={`Toggle ${target.Name}`} showCollapse={machineResources && machineResources.Resources.length > 0} isOpen={open} onClick={() => setOpen(!open)}/>
                        <InternalLink to={linkToMachine} showOpensInNewTabIcon={true} openInSelf={false}>
                            <span className={styles.machineText}>{target.Name}</span>
                        </InternalLink>
                    </div>
                </DataTableRowColumn>
                <DataTableRowColumn />
                <DataTableRowColumn className={styles.centredTableContent}>
                    <div className={cx(styles.multiItemCellFlexContainer, styles.fixedWidthStatus)}>
                        <MachineStatusIcon status={machineResources.Status} aria-labelledby={`${machineResources.MachineId}status`}/>
                        <p className={cx(styles.tableCellText, styles.leftPad)} id={`${machineResources.MachineId}status`}>
                            {machineResources.Status}
                        </p>
                    </div>
                </DataTableRowColumn>
            </DataTableRow>
            {open &&
            machineResources &&
            machineResources.Resources.sort(resourceSortFunctions.byName).map((resource, index) => (<Row resource={resource} key={`${target.Id}child${index}`} rowKey={`${target.Id}-child-${index}`} indentLevel={1} resourceSelected={resourceSelected}/>))}
        </>);
}
function Row({ indentLevel, rowKey, resource, resourceSelected }: {
    indentLevel: number;
    rowKey: string;
    resource: KubernetesLiveStatusResource;
    resourceSelected: OnResourceSelected;
}) {
    const shouldBeExpanded = isFailingHealthStatus(resource.HealthStatus);
    const [open, setOpen] = useAutoExpansion(shouldBeExpanded);
    return (<>
            <DataTableRow displayNoBorder={true}>
                <DataTableRowColumn fullWidth={true} className={styles.centredTableContent}>
                    <NameAndSymbols resource={resource} indentLevel={indentLevel} isOpen={open} onClick={() => setOpen(!open)} resourceSelected={resourceSelected}/>
                </DataTableRowColumn>
                <TextCell text={resource.Kind}/>
                <TextCell text=""/>
                <TextCell text={resource.Namespace ? resource.Namespace : emptyNamespaceDisplay}/>
                <DataTableRowColumn className={styles.centredTableContent}>
                    {resource.SyncStatus && (<div className={cx(styles.multiItemCellFlexContainer, styles.fixedWidthStatus)}>
                            <LiveStatusResourceIcon status={resource.SyncStatus} aria-labelledby={`${rowKey}status`}/>
                            <p className={cx(styles.tableCellText, styles.leftPad)} id={`${rowKey}status`}>
                                {resource.SyncStatus}
                            </p>
                        </div>)}
                </DataTableRowColumn>
                <DataTableRowColumn className={styles.centredTableContent}>
                    <div data-chameleon-tour="object-live-status" className={cx(styles.multiItemCellFlexContainer, styles.fixedWidthStatus)}>
                        <LiveStatusResourceIcon status={resource.HealthStatus} aria-labelledby={`${rowKey}status`}/>
                        <p className={cx(styles.tableCellText, styles.leftPad)} id={`${rowKey}status`}>
                            {resource.HealthStatus}
                        </p>
                    </div>
                </DataTableRowColumn>
            </DataTableRow>
            {open &&
            resource.Children.sort(resourceSortFunctions.byName).map((resource, index) => (<Row resource={resource} key={`${rowKey}child${index}`} rowKey={`${rowKey}-child-${index}`} indentLevel={indentLevel + 1} resourceSelected={resourceSelected}/>))}
        </>);
}
function TextCell({ text }: {
    text: string;
}) {
    return (<DataTableRowColumn className={styles.centredTableContent}>
            <p className={styles.tableCellText}>{text}</p>
        </DataTableRowColumn>);
}
function NameAndSymbols({ indentLevel, isOpen, onClick, resource, resourceSelected }: {
    resource: KubernetesLiveStatusResource;
    indentLevel: number;
    isOpen: boolean;
    onClick: () => void;
    resourceSelected: OnResourceSelected;
}) {
    const pixelsPerIndent = 40;
    const indent = pixelsPerIndent * indentLevel;
    const hasChildNodes = resource.Children.length > 0;
    return (<div className={cx(styles.multiItemCellFlexContainer, styles.indented(indent))}>
            <CollapseIconOrSpacer name={`Toggle ${resource.Name}`} showCollapse={hasChildNodes} isOpen={isOpen} onClick={onClick}/>
            <div className={cx(styles.multiItemCellFlexContainer, styles.leftPad)}>
                <K8sResourceIcon resourceType={resource.Kind}/>
            </div>
            <div className={cx(styles.tableCellText, styles.leftPad)}>
                {resource.HealthStatus === "Missing" || resource.HealthStatus === "Unknown" ? (<span>{resource.Name}</span>) : (<a data-chameleon-tour="object-drawer-link" className={styles.linkStyle} onClick={() => resourceSelected(resource)}>
                        {resource.Name}
                    </a>)}
            </div>
        </div>);
}
function CollapseIconOrSpacer({ name, isOpen, onClick, showCollapse }: {
    name: string;
    showCollapse: boolean;
    isOpen: boolean;
    onClick: () => void;
}) {
    const iconHeight = 24;
    const iconWidth = 48;
    if (showCollapse) {
        return (<div className={styles.iconPadding}>
                <IconButton onClick={onClick} icon={isOpen ? "ChevronDownIcon" : "ChevronRightIcon"} accessibleName={name}/>
            </div>);
    }
    else {
        return <div className={styles.iconSpacer(iconHeight, iconWidth)}/>;
    }
}
const styles = {
    machineRow: css({
        background: themeTokens.color.background.secondary.default,
    }),
    machineText: css({
        font: text.body.regular.medium,
        margin: 0,
    }),
    multiItemCellFlexContainer: css({
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "0",
        height: "100%",
    }),
    tableCellText: css({
        font: text.body.regular.medium,
        display: "inline-block",
        margin: 0,
    }),
    leftPad: css({
        paddingLeft: space[8],
    }),
    centredTableContent: css({
        "&&": { verticalAlign: "middle" },
    }),
    fixedWidthStatus: css({
        width: "105px", //this is literally the width of a "Progressing" word and icon
    }),
    headerRow: css({
        color: themeTokens.color.text.secondary,
        font: text.heading.xSmall,
        margin: 0,
        textTransform: "uppercase",
    }),
    iconPadding: css({
        paddingLeft: space[12],
        paddingRight: space[12],
    }),
    indented(pixels: number) {
        return css({ paddingLeft: pixels });
    },
    iconSpacer(height: number, width: number) {
        return css({
            height: height,
            flexBasis: width,
            flexGrow: 0,
            flexShrink: 0,
        });
    },
    linkStyle: css({
        cursor: "pointer",
    }),
};
